/**
 * TeamAnalytics - Tufte-inspired team analytics for NBA, NHL, NFL.
 *
 * Composes sport-agnostic Tufte components with sport-aware metric mapping.
 * NCAAB uses its own NCAABAnalyticsContent (unchanged).
 *
 * @param {Object} game - Full game summary from /{sport}/games/{id}/summary
 * @param {string} sport - Sport identifier (nba, nhl, nfl, ncaa-f)
 */
import { useMemo } from 'react';
import {
  GamePerformanceStripCard,
  PredictionPanel,
  TeamStrengthComparison,
  ScoringTrendStrip,
  SmallMultiplesGrid,
  BettingContextStrip,
} from './tufte';
import { HotStreaksStrip } from './tufte';
import Card from '../common/Card';

const TeamAnalytics = ({ game, sport = 'nba' }) => {
  const homeTeam = game?.home_team;
  const awayTeam = game?.away_team;
  const homeStats = game?.home_stats;
  const awayStats = game?.away_stats;
  const predictions = game?.predictions;
  const h2h = game?.head_to_head;
  const hotPlayers = game?.hot_players;
  const isHockey = sport === 'nhl';
  const isBaseball = sport === 'mlb';

  // --- Sparkline trend data from recent games ---
  const homePpgTrend = useMemo(() => {
    return (homeTeam?.recent_games || []).map(g => g.team_score).filter(Boolean);
  }, [homeTeam]);

  const awayPpgTrend = useMemo(() => {
    return (awayTeam?.recent_games || []).map(g => g.team_score).filter(Boolean);
  }, [awayTeam]);

  const homePapgTrend = useMemo(() => {
    return (homeTeam?.recent_games || []).map(g => g.opp_score).filter(Boolean);
  }, [homeTeam]);

  const awayPapgTrend = useMemo(() => {
    return (awayTeam?.recent_games || []).map(g => g.opp_score).filter(Boolean);
  }, [awayTeam]);

  const homeDiffTrend = useMemo(() => {
    return (homeTeam?.recent_games || [])
      .filter(g => g.team_score != null && g.opp_score != null)
      .map(g => g.team_score - g.opp_score);
  }, [homeTeam]);

  const awayDiffTrend = useMemo(() => {
    return (awayTeam?.recent_games || [])
      .filter(g => g.team_score != null && g.opp_score != null)
      .map(g => g.team_score - g.opp_score);
  }, [awayTeam]);

  // --- Helper to get PPG/PAPG across sports ---
  const getTeamPpg = (stats) => {
    const s = stats?.season || {};
    return s.ppg || s.points_for_avg || s.goals_for_avg || 0;
  };
  const getTeamPapg = (stats) => {
    const s = stats?.season || {};
    return s.papg || s.points_against_avg || s.goals_against_avg || 0;
  };

  // --- TeamStrengthComparison metrics (sport-aware) ---
  const strengthMetrics = useMemo(() => {
    if (isHockey) {
      const ha = homeStats?.advanced || {};
      const aa = awayStats?.advanced || {};
      const hr = homeStats?.rankings || {};
      const ar = awayStats?.rankings || {};
      return [
        { label: 'SOS Rank', homeValue: hr.sos_rank, awayValue: ar.sos_rank, min: 1, max: 32, lowerIsBetter: true, format: v => v != null ? `#${v}` : '-' },
        { label: 'PP%', homeValue: ha.power_play_pct, awayValue: aa.power_play_pct, min: 10, max: 35, format: v => v != null ? `${v.toFixed(1)}%` : '-' },
        { label: 'PK%', homeValue: ha.penalty_kill_pct, awayValue: aa.penalty_kill_pct, min: 70, max: 95, format: v => v != null ? `${v.toFixed(1)}%` : '-' },
        { label: 'FO%', homeValue: ha.faceoff_pct, awayValue: aa.faceoff_pct, min: 45, max: 55, format: v => v != null ? `${v.toFixed(1)}%` : '-' },
        { label: 'Save%', homeValue: ha.save_pct, awayValue: aa.save_pct, min: 0.880, max: 0.935, format: v => v != null ? `.${(v * 1000).toFixed(0)}` : '-' },
        { label: 'Shots/Game', homeValue: ha.shots_per_game, awayValue: aa.shots_per_game, min: 25, max: 38, format: v => v != null ? v.toFixed(1) : '-' },
      ];
    }

    if (isBaseball) {
      const ha = homeStats?.advanced || {};
      const aa = awayStats?.advanced || {};
      const hr = homeStats?.rankings || {};
      const ar = awayStats?.rankings || {};
      return [
        { label: 'SOS Rank', homeValue: hr.sos_rank, awayValue: ar.sos_rank, min: 1, max: 30, lowerIsBetter: true, format: v => v != null ? `#${v}` : '-' },
        { label: 'R/Game', homeValue: ha.runs_per_game, awayValue: aa.runs_per_game, min: 2, max: 7, format: v => v != null ? v.toFixed(2) : '-' },
        { label: 'RA/Game', homeValue: ha.runs_against_per_game, awayValue: aa.runs_against_per_game, min: 2, max: 7, lowerIsBetter: true, format: v => v != null ? v.toFixed(2) : '-' },
        { label: 'ERA', homeValue: ha.era, awayValue: aa.era, min: 2.5, max: 6.0, lowerIsBetter: true, format: v => v != null ? v.toFixed(2) : '-' },
        { label: 'WHIP', homeValue: ha.whip, awayValue: aa.whip, min: 0.9, max: 1.6, lowerIsBetter: true, format: v => v != null ? v.toFixed(2) : '-' },
        { label: 'K/9', homeValue: ha.k_per_9, awayValue: aa.k_per_9, min: 5, max: 12, format: v => v != null ? v.toFixed(1) : '-' },
      ];
    }

    // NBA / default
    const ha = homeStats?.advanced || {};
    const aa = awayStats?.advanced || {};
    const hr = homeStats?.rankings || {};
    const ar = awayStats?.rankings || {};
    return [
      { label: 'SOS Rank', homeValue: hr.sos_rank, awayValue: ar.sos_rank, min: 1, max: 30, lowerIsBetter: true, format: v => v != null ? `#${v}` : '-' },
      { label: 'Quality W', homeValue: hr.quality_wins, awayValue: ar.quality_wins, min: 0, max: 20, format: v => v != null ? `${v}` : '-' },
      { label: 'Off Rating', homeValue: ha.offensive_rating, awayValue: aa.offensive_rating, min: 100, max: 125, format: v => v != null ? v.toFixed(1) : '-' },
      { label: 'Def Rating', homeValue: ha.defensive_rating, awayValue: aa.defensive_rating, min: 100, max: 125, lowerIsBetter: true, format: v => v != null ? v.toFixed(1) : '-' },
      { label: 'Net Rating', homeValue: ha.net_rating, awayValue: aa.net_rating, min: -15, max: 15, format: v => v != null ? (v > 0 ? `+${v.toFixed(1)}` : v.toFixed(1)) : '-' },
      { label: 'Pace', homeValue: ha.pace, awayValue: aa.pace, min: 90, max: 110, format: v => v != null ? v.toFixed(1) : '-' },
    ];
  }, [homeStats, awayStats, isHockey, isBaseball]);

  // --- ScoringTrendStrip rows ---
  const scoringRows = useMemo(() => {
    const hPpg = getTeamPpg(homeStats);
    const aPpg = getTeamPpg(awayStats);
    const hPapg = getTeamPapg(homeStats);
    const aPapg = getTeamPapg(awayStats);
    const scoringLabel = isHockey ? 'Goals' : isBaseball ? 'Runs' : 'PPG';
    const allowedLabel = isHockey ? 'GA' : isBaseball ? 'RA' : 'PAPG';

    return [
      { label: scoringLabel, homeValue: hPpg, awayValue: aPpg, homeSparkData: homePpgTrend, awaySparkData: awayPpgTrend },
      { label: allowedLabel, homeValue: hPapg, awayValue: aPapg, homeSparkData: homePapgTrend, awaySparkData: awayPapgTrend },
      {
        label: 'Diff',
        homeValue: Math.round((hPpg - hPapg) * 10) / 10,
        awayValue: Math.round((aPpg - aPapg) * 10) / 10,
        homeSparkData: homeDiffTrend,
        awaySparkData: awayDiffTrend,
        homeColor: hPpg - hPapg > 0 ? 'text-success' : 'text-danger',
        awayColor: aPpg - aPapg > 0 ? 'text-success' : 'text-danger',
      },
    ];
  }, [homeStats, awayStats, isHockey, isBaseball, homePpgTrend, awayPpgTrend, homePapgTrend, awayPapgTrend, homeDiffTrend, awayDiffTrend]);

  // --- SmallMultiplesGrid: advanced metrics ---
  const advancedMetrics = useMemo(() => {
    if (isHockey) {
      const ha = homeStats?.advanced || {};
      const aa = awayStats?.advanced || {};
      return [
        { label: 'PP%', homeValue: ha.power_play_pct, awayValue: aa.power_play_pct, max: 35, format: v => v != null ? `${v.toFixed(1)}%` : '-' },
        { label: 'FO%', homeValue: ha.faceoff_pct, awayValue: aa.faceoff_pct, max: 60, format: v => v != null ? `${v.toFixed(1)}%` : '-' },
        { label: 'Shots/G', homeValue: ha.shots_per_game, awayValue: aa.shots_per_game, max: 40, format: v => v != null ? v.toFixed(1) : '-' },
        { label: 'SA/G', homeValue: ha.shots_against_per_game, awayValue: aa.shots_against_per_game, max: 40, format: v => v != null ? v.toFixed(1) : '-' },
        { label: 'Hits/G', homeValue: ha.hits_per_game, awayValue: aa.hits_per_game, max: 35, format: v => v != null ? v.toFixed(1) : '-' },
        { label: 'Blocks/G', homeValue: ha.blocks_per_game, awayValue: aa.blocks_per_game, max: 25, format: v => v != null ? v.toFixed(1) : '-' },
      ];
    }

    if (isBaseball) {
      const ha = homeStats?.advanced || {};
      const aa = awayStats?.advanced || {};
      return [
        { label: 'H/Game', homeValue: ha.hits_per_game, awayValue: aa.hits_per_game, max: 12, format: v => v != null ? v.toFixed(1) : '-' },
        { label: 'HA/Game', homeValue: ha.hits_against_per_game, awayValue: aa.hits_against_per_game, max: 12, lowerIsBetter: true, format: v => v != null ? v.toFixed(1) : '-' },
        { label: 'HR/Game', homeValue: ha.hr_per_game, awayValue: aa.hr_per_game, max: 3, format: v => v != null ? v.toFixed(1) : '-' },
        { label: 'BB/9', homeValue: ha.bb_per_9, awayValue: aa.bb_per_9, max: 5, lowerIsBetter: true, format: v => v != null ? v.toFixed(1) : '-' },
        { label: 'E/Game', homeValue: ha.errors_per_game, awayValue: aa.errors_per_game, max: 3, lowerIsBetter: true, format: v => v != null ? v.toFixed(2) : '-' },
      ];
    }

    // NBA / default
    const ha = homeStats?.advanced || {};
    const aa = awayStats?.advanced || {};
    const hs = homeStats?.shooting || {};
    const as_ = awayStats?.shooting || {};
    const pctFormat = v => v != null ? `${(v * 100).toFixed(1)}%` : '-';
    return [
      { label: 'eFG%', homeValue: ha.effective_fg_pct, awayValue: aa.effective_fg_pct, max: 0.65, format: pctFormat },
      { label: 'TS%', homeValue: ha.true_shooting_pct, awayValue: aa.true_shooting_pct, max: 0.65, format: pctFormat },
      { label: 'FG%', homeValue: hs.fg_pct, awayValue: as_.fg_pct, max: 0.55, format: pctFormat },
      { label: '3P%', homeValue: hs.three_pct, awayValue: as_.three_pct, max: 0.45, format: pctFormat },
      { label: 'FT%', homeValue: hs.ft_pct, awayValue: as_.ft_pct, max: 0.90, format: pctFormat },
      { label: 'OReb%', homeValue: ha.offensive_rebound_pct, awayValue: aa.offensive_rebound_pct, max: 35, format: v => v != null ? `${v.toFixed(1)}%` : '-' },
      { label: 'DReb%', homeValue: ha.defensive_rebound_pct, awayValue: aa.defensive_rebound_pct, max: 80, format: v => v != null ? `${v.toFixed(1)}%` : '-' },
      { label: 'A/TO', homeValue: ha.assist_to_turnover, awayValue: aa.assist_to_turnover, max: 2.5, format: v => v != null ? v.toFixed(2) : '-' },
      { label: 'TO%', homeValue: ha.turnover_pct, awayValue: aa.turnover_pct, max: 25, format: v => v != null ? `${v.toFixed(1)}%` : '-' },
    ];
  }, [homeStats, awayStats, isHockey, isBaseball]);

  // --- BettingContextStrip records ---
  const bettingRecords = useMemo(() => {
    const hs = homeStats?.season || {};
    const as_ = awayStats?.season || {};
    return [
      {
        label: 'Against the Spread',
        wins: hs.ats_wins, losses: hs.ats_losses, pushes: hs.ats_pushes,
        awayWins: as_.ats_wins, awayLosses: as_.ats_losses, awayPushes: as_.ats_pushes,
      },
      {
        label: 'Over/Under',
        wins: hs.over_count, losses: hs.under_count, pushes: hs.push_count,
        awayWins: as_.over_count, awayLosses: as_.under_count, awayPushes: as_.push_count,
      },
    ];
  }, [homeStats, awayStats]);

  if (!game) return null;

  return (
    <div className="space-y-4">
      {/* 1. Game Performance Strips - away | home */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <GamePerformanceStripCard
          team={awayTeam}
          stats={awayStats}
          momentum={awayTeam?.momentum}
          recentGames={awayTeam?.recent_games || []}
          ppgTrend={awayPpgTrend}
        />
        <GamePerformanceStripCard
          team={homeTeam}
          stats={homeStats}
          momentum={homeTeam?.momentum}
          recentGames={homeTeam?.recent_games || []}
          ppgTrend={homePpgTrend}
        />
      </div>

      {/* 2. Strength Comparison + Scoring Trends side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TeamStrengthComparison
          metrics={strengthMetrics}
          homeTeam={homeTeam}
          awayTeam={awayTeam}
        />
        <ScoringTrendStrip
          rows={scoringRows}
          homeTeam={homeTeam}
          awayTeam={awayTeam}
        />
      </div>

      {/* 3. Advanced metrics small multiples */}
      <SmallMultiplesGrid
        metrics={advancedMetrics}
        homeTeam={homeTeam}
        awayTeam={awayTeam}
      />

      {/* 4. Betting context + Head-to-Head */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BettingContextStrip
          records={bettingRecords}
          homeTeam={homeTeam}
          awayTeam={awayTeam}
        />

        {h2h && (h2h.wins > 0 || h2h.losses > 0) && (
          <Card className="p-3">
            <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">
              Head-to-Head
            </h3>
            <div className="flex items-center gap-4 text-xs">
              <span className="text-text-secondary">
                {awayTeam?.abbreviation || 'Away'}: <span className="font-mono font-semibold text-text-primary">{h2h.losses}</span> wins
              </span>
              <span className="text-text-secondary">
                {homeTeam?.abbreviation || 'Home'}: <span className="font-mono font-semibold text-text-primary">{h2h.wins}</span> wins
              </span>
              {h2h.last_date && (
                <span className="text-text-secondary text-[10px] opacity-60 ml-auto">
                  Last: {h2h.last_date} ({h2h.last_result === 'W' ? homeTeam?.abbreviation : awayTeam?.abbreviation} won)
                </span>
              )}
            </div>
          </Card>
        )}
      </div>

      {/* 5. Hot Streaks Strip */}
      {hotPlayers && hotPlayers.length > 0 && (
        <HotStreaksStrip
          hotPlayers={hotPlayers}
          homeTeam={homeTeam}
          awayTeam={awayTeam}
        />
      )}

      {/* 6. Predictions (premium-gated) */}
      {predictions && (
        <PredictionPanel
          predictions={predictions}
          spread={game.spread}
          spreadHomeOdds={game.spread_home_odds}
          spreadAwayOdds={game.spread_away_odds}
          overUnder={game.over_under}
          overOdds={game.over_odds}
          underOdds={game.under_odds}
          homeMoneyline={game.home_moneyline}
          awayMoneyline={game.away_moneyline}
          homeTeam={homeTeam}
          awayTeam={awayTeam}
        />
      )}
    </div>
  );
};

export default TeamAnalytics;
